import React, { useContext, useMemo, useState } from 'react'
import { OrderContext } from '@ui/contexts'
import css from 'styled-jsx/css'
import { dateParse } from '@ui/helpers/time'
import { sleep, to17TrackLink, iconPath } from '@ui/helpers'
import ShTimeline from '@ui/components/shared/Timeline'
import Loading from '@ui/components/shared/Loading'
import { TrackingItem } from '@ui/types/interfaces'

type DoInfoTracking = {
  dataTracking: Types.ParcelInfo
}

type DisplayTracking = {
  last_logs?: Types.PoItemActivity[]
  trackings: string[]
}

const getTrackingLogInfo = (log: Types.PoItemActivity) => {
  const hours = dateParse(log.created!, 'HH:mm')
  const date = dateParse(log.created!, 'MM-dd-yyyy')
  return { hours, date, message: log.message }
}

// Output: Array object that has list tracking number, last log
function groupTrackings(
  tracking_numbers: Types.TrackingItem[],
  last_tracking_number: string
): DisplayTracking[] {
  const result = []
  let currentIndex = 0
  for (const tracking of tracking_numbers) {
    // Init list
    // debugger
    //only get last tracking info.
    if (tracking.tracking_number != last_tracking_number) {
      continue
    }

    if (!result.length || result.length <= currentIndex) {
      result.push({
        trackings: [tracking.tracking_number]
      } as DisplayTracking)
    }

    if (tracking.last_logs?.length) {
      result[currentIndex].last_logs = tracking.last_logs
      if (!result[currentIndex].trackings.includes(tracking.tracking_number!)) {
        result[currentIndex].trackings.push(tracking.tracking_number!)
      }
      currentIndex++
    } else if (!result[currentIndex].trackings.includes(tracking.tracking_number!)) {
      result[currentIndex].trackings.push(tracking.tracking_number!)
    }
  }

  return result
}

export const DoInfoTracking: React.FC<DoInfoTracking> = ({ dataTracking }) => {
  const { orderDetail } = useContext(OrderContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [maxLogs, setMaxLogs] = useState<number>(3)

  async function onLoadMore(logs: Types.PoItemActivity[]) {
    setIsLoading(true)
    const total = logs.length
    await sleep(500).then(() => setIsLoading(false))
    if (total - maxLogs > 5) {
      setMaxLogs((maxLogs) => maxLogs + 5)
    } else {
      setMaxLogs(total)
    }
  }

  return useMemo(() => {
    const item = dataTracking!.tracking_numbers
    const displayTrackings = groupTrackings(
      item || ({} as TrackingItem[]),
      dataTracking!.tracking_date?.tracking_number || ''
    )

    return (
      <>
        <style jsx>{infoTrackingStyle}</style>
        {isLoading && <Loading />}
        <div className="do-tracking">
          {displayTrackings?.length ? (
            displayTrackings.map((tracking: DisplayTracking, index: number) => (
              <div
                className={`do-tracking__logs mb-4 ${index === 0 ? 'mt-6' : 'mb-4'}`}
                key={index}
              >
                <div
                  className="has-text-weight-semibold is-flex"
                  style={{ whiteSpace: 'break-spaces' }}
                >
                  Tracking
                  <div className="ml-2">
                    {tracking.trackings.map((tracking_number: string, i: number) => (
                      <div
                        key={i}
                        className={`is-flex is-align-items-center ${
                          tracking_number?.includes('old') ? ' is-old' : ''
                        }`}
                      >
                        <span style={{ color: '#657381' }} className="mr-1">
                          {tracking_number?.replace('old-', '')}
                        </span>
                        {i == tracking.trackings.length - 1 && (
                          <div
                            className="has-pointer is-flex"
                            onClick={() => window.open(to17TrackLink(tracking_number!), '_blank')}
                          >
                            <figure className="image is-16x16">
                              <img src={iconPath('go-to.svg')} alt="launch" />
                            </figure>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {tracking.last_logs?.length && (
                  <>
                    <ShTimeline styleType={['no-headers']} className={`mt-4`}>
                      {tracking.last_logs
                        .slice(0, maxLogs + 1)
                        .map((log: Types.PoItemActivity, index: number) => (
                          <div
                            key={index}
                            className={`timeline-item ${
                              index <= maxLogs - 1 ? '' : 'simple-content'
                            }`}
                          >
                            {index <= maxLogs - 1 ? (
                              <>
                                <div className={`timeline-marker ${index ? 'default' : ''}`}>
                                  {!index ? (
                                    <figure className="image">
                                      <img
                                        src={iconPath(`timeline-active.svg`)}
                                        alt="active timeline"
                                      />
                                    </figure>
                                  ) : null}
                                </div>
                                <div className="timeline-content">
                                  <p className="tracking-message">{log.location ?? 'N/A'}</p>
                                  <p className="tracking-message">{log.message}</p>
                                  <span className="tracking-time mr-8">
                                    {getTrackingLogInfo(log).date}
                                  </span>
                                  <span className="tracking-time">
                                    {getTrackingLogInfo(log).hours}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <div className="timeline-content">
                                <a
                                  className="is-link"
                                  onClick={() => onLoadMore(tracking.last_logs!)}
                                >
                                  View more
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                    </ShTimeline>
                  </>
                )}
              </div>
            ))
          ) : (
            <p className="has-text-weight-semibold mt-8 ml-3">
              Tracking: <span style={{ color: '#657381' }}>Not available</span>
            </p>
          )}
        </div>
      </>
    )
  }, [orderDetail.id, maxLogs, isLoading])
}

const infoTrackingStyle = css`
  .do-tracking {
    font-size: 12px;
    line-height: 24px;

    &__logs {
      padding: 16px 24px;
      border: 1px solid #eaecf0;
      box-sizing: border-box;
      border-radius: var(--round-8);
    }

    .has-pointer {
      cursor: pointer;
    }

    .tracking-message {
      font-size: 12px;
      line-height: 24px;
      color: #657381;
    }

    .tracking-time {
      font-size: 10px;
      line-height: 16px;
      color: #c5cdd5;
    }

    .is-link {
      color: #0b92f1;
      cursor: pointer;
    }

    .is-old {
      text-decoration: line-through;
      opacity: 0.6;
    }
  }
`
